import React from 'react'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { State, PartialAction } from 'src/types'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { SelectCardWithImage } from 'src/Components/Ui/SelectCardWithImage'
import { webPartsPositionFilter } from 'src/logic'
import {
  baseCautionGenerator,
  baseDescriptionGenerator,
  indexFingerObjectsSwitcher,
  seriesObjectsGenerator,
  ysrFlagGenerator
} from 'src/features/yellStory/logic/baseSetting'
import { useDispatcher } from 'src/features/yellStory/logic/dispatchGenerator'
import {
  dominantArmObjs,
  sizeObjs,
  fingerGuardTypeObjs,
  webPartsObjs,
  materialPackObjs,
  leatherIntegratedMiddleObjs,
  leatherIntegratedRingObjs,
  SERIES_BUTTON_OPTION,
  DOMINANT_ARM_BUTTON_OPTION,
  SIZE_BUTTON_OPTION,
  FINGER_GUARD_TYPE_BUTTON_OPTION,
  LEATHER_INTEGRATED_MIDDLE_BUTTON_OPTION,
  LEATHER_INTEGRATED_RING_BUTTON_OPTION,
  MATERIAL_PACK_BUTTON_OPTION,
  WEB_PARTS_BUTTON_OPTION,
  LEATHER_INTEGRATED_INDEX_BUTTON_OPTION
} from 'src/features/yellStory/constants/bases'
import { useLocale } from 'src/locales/useLocale'

type Props = {
  state: State
  selectedIndex: number
  dispatch: React.Dispatch<PartialAction>
}

export const BaseSetter: React.FC<Props> = ({ state, selectedIndex, dispatch }) => {
  const { t } = useLocale()
  const {
    series,
    dominantArm,
    size,
    fingerGuardType,
    leatherIntegratedIndex,
    leatherIntegratedMiddle,
    leatherIntegratedRing,
    materialPack,
    webParts
  } = state
  const handle = {
    series: useDispatcher('series', dispatch),
    dominantArm: useDispatcher('dominantArm', dispatch),
    size: useDispatcher('size', dispatch),
    fingerGuardType: useDispatcher('fingerGuardType', dispatch),
    leatherIntegratedIndex: useDispatcher('leatherIntegratedIndex', dispatch),
    leatherIntegratedMiddle: useDispatcher('leatherIntegratedMiddle', dispatch),
    leatherIntegratedRing: useDispatcher('leatherIntegratedRing', dispatch),
    materialPack: useDispatcher('materialPack', dispatch),
    webParts: useDispatcher('webParts', dispatch)
  }
  const {
    isDefaultSize,
    isSelectedFingerGuard,
    isIndexOption,
    isMiddleOption,
    isRingOption,
    isSelectedMiddleFingerEmbroidery,
    isSelectedRingFingerEmbroidery,
    isIndexFingerGuard,
    isJuniorLine,
    isSelectedSeries
  } = ysrFlagGenerator(state)
  const seriesObjects = seriesObjectsGenerator(isJuniorLine)
  return (
    <TabPanel selectedIndex={selectedIndex} index={0}>
      <SelectCard
        summary={t.SERIES}
        selectedLabel={series?.label}
        objects={seriesObjects}
        isError={state.series?.value === 'unselected'}
        handleChange={handle.series}
        disabled={isSelectedSeries}
        description={baseDescriptionGenerator(isSelectedSeries, 'series')}
        className={SERIES_BUTTON_OPTION}
      />
      <SelectCard
        summary={t.DOMINANT_ARM}
        selectedLabel={dominantArm?.label}
        objects={dominantArmObjs}
        isError={dominantArm?.value === 'unselected'}
        handleChange={handle.dominantArm}
        className={DOMINANT_ARM_BUTTON_OPTION}
        defaultExpanded={false}
      />
      <SelectCard
        summary={t.FINGER_SIZE}
        selectedLabel={size.label}
        objects={sizeObjs}
        isError={state.size?.value === 'unselected'}
        handleChange={handle.size}
        caution={baseCautionGenerator(!isDefaultSize, 'size')}
        className={SIZE_BUTTON_OPTION}
      />
      <SelectCardWithImage
        summary={t.FINGER_PAD}
        selectedLabel={fingerGuardType.label}
        objects={fingerGuardTypeObjs}
        isError={state.fingerGuardType?.value === 'unselected'}
        handleChange={handle.fingerGuardType}
        disabled={isIndexOption}
        description={baseDescriptionGenerator(isIndexOption, 'fingerGuardType')}
        imageHeight={80}
        imageWidth={140}
        isShowLabel
        className={FINGER_GUARD_TYPE_BUTTON_OPTION}
      />
      {/* [革一体形状]はオプションで別料金発生 */}
      <SelectCardWithImage
        summary={t.LEATHER_INTEGRATED_INDEX}
        selectedLabel={leatherIntegratedIndex?.label}
        objects={indexFingerObjectsSwitcher(isIndexFingerGuard)}
        isError={leatherIntegratedIndex?.value === 'unselected'}
        handleChange={handle.leatherIntegratedIndex}
        disabled={!isSelectedFingerGuard}
        description={baseDescriptionGenerator(!isSelectedFingerGuard, 'leatherIntegratedIndex')}
        caution={baseCautionGenerator(isIndexOption, 'leatherIntegratedIndex')}
        imageHeight={100}
        imageWidth={150}
        isShowLabel
        className={LEATHER_INTEGRATED_INDEX_BUTTON_OPTION}
      />
      <SelectCardWithImage
        summary={t.LEATHER_INTEGRATED_MIDDLE}
        selectedLabel={leatherIntegratedMiddle?.label}
        objects={leatherIntegratedMiddleObjs}
        isError={leatherIntegratedMiddle?.value === 'unselected'}
        handleChange={handle.leatherIntegratedMiddle}
        caution={baseCautionGenerator(isMiddleOption, 'leatherIntegratedMiddle')}
        disabled={isSelectedMiddleFingerEmbroidery}
        imageHeight={100}
        imageWidth={150}
        isShowLabel
        className={LEATHER_INTEGRATED_MIDDLE_BUTTON_OPTION}
      />
      <SelectCardWithImage
        summary={t.LEATHER_INTEGRATED_RING}
        selectedLabel={leatherIntegratedRing?.label}
        objects={leatherIntegratedRingObjs}
        isError={leatherIntegratedRing?.value === 'unselected'}
        handleChange={handle.leatherIntegratedRing}
        caution={baseCautionGenerator(isRingOption, 'leatherIntegratedRing')}
        disabled={isSelectedRingFingerEmbroidery}
        imageHeight={100}
        imageWidth={150}
        isShowLabel
        className={LEATHER_INTEGRATED_RING_BUTTON_OPTION}
      />
      <SelectCardWithImage
        summary={t.MATERIAL_PACK}
        selectedLabel={materialPack?.label}
        objects={materialPackObjs}
        isError={materialPack?.value === 'unselected'}
        handleChange={handle.materialPack}
        imageHeight={60}
        imageWidth={170}
        className={MATERIAL_PACK_BUTTON_OPTION}
      />
      <SelectCardWithImage
        summary={t.WEB}
        selectedLabel={webParts?.label}
        objects={webPartsPositionFilter(webPartsObjs, 'infielder')}
        isError={webParts?.value === 'unselected'}
        handleChange={handle.webParts}
        className={WEB_PARTS_BUTTON_OPTION}
      />
    </TabPanel>
  )
}
